/**
 * Clinical validation and the human-confirmation gate.
 *
 * The concept paper's safety principle is that a record surfaces for worker
 * confirmation rather than writing silently, and that low-confidence readings
 * are never auto-committed. That rule is enforced HERE, and this module is
 * compiled into both the device and the server, so it holds even if a client is
 * modified or replaced.
 *
 * Two kinds of blocking flag, and the difference matters:
 *
 *   - A flag that is `resolvable` records something that can be TRUE of a real
 *     card: Penta-3 genuinely given 18 days after Penta-2. The worker can accept
 *     it, with a stated consequence ("this dose does not count and will be
 *     repeated"), and commit.
 *
 *   - A flag that is not resolvable records something that CANNOT be true: a
 *     dose before birth, a date in the future, a date nobody can read, the same
 *     vaccine twice. There is no acknowledgement that makes those right. The
 *     only way past them is to correct the data, at which point the flag stops
 *     firing on its own.
 *
 * And a resolution only counts if it is one that settles the matter. "Referring
 * to the supervisor" is a legitimate thing to record, and it deliberately does
 * NOT unlock the commit — a referral is the opposite of a decision.
 */
import {
  SCHEDULE, SCHEDULE_BY_KEY, MIN_INTERVAL_DAYS, EARLY_TOLERANCE_DAYS,
  daysBetween, ageLabel, overdueDoses, scheduledDateFor, isIsoDate, type DoseInput
} from './schedule.js';

/** Extraction confidence below this must be read back from the card by a worker. */
export const CONFIDENCE_FLOOR = 0.70;
/** Between the floor and this, the field is shown for verification but not blocking. */
export const CONFIDENCE_VERIFY = 0.90;

export type Severity = 'conflict' | 'warning' | 'info';

export interface Flag {
  code: string;
  severity: Severity;
  title: string;
  detail: string;
  /** Must be cleared before the record can commit. */
  blocking: boolean;
  /**
   * Whether a worker can clear this by accepting it. False for anything that
   * cannot be true of a real card: those clear only when the data is corrected.
   */
  resolvable: boolean;
}

/**
 * What a worker decided about a flag. Only some kinds settle it.
 *
 *   accept-invalid  — the card is right; the dose does not count; it will be repeated
 *   confirmed-none  — confirmed with the caregiver that the child has had no doses
 *   history-elsewhere — a replacement card; the history is already in the e-Tracker
 *   referred        — sent to the EPI supervisor. Recorded, but settles nothing.
 *   correction      — a log of how data was fixed (a date re-read, a registry
 *                     link). Settles nothing by itself: the corrected data is
 *                     what makes the flag stop firing.
 */
export type ResolutionKind = 'accept-invalid' | 'confirmed-none' | 'history-elsewhere' | 'referred' | 'correction';

export const SETTLING_KINDS: readonly ResolutionKind[] = ['accept-invalid', 'confirmed-none', 'history-elsewhere'];

export interface ResolutionInput {
  flagCode: string;
  kind: ResolutionKind;
}

export interface FieldState {
  key: string;
  label: string;
  value: string;
  confidence: number;
  resolved: boolean;
  edited: boolean;
}

export interface DoseState extends DoseInput {
  confidence: number;
  /** A low-confidence dose date the worker has read back from the card. */
  resolved?: boolean;
}

export interface RecordForValidation {
  id: string;
  fields: FieldState[];
  doses: DoseState[];
  newEnrolment: boolean;
  resolutions: ResolutionInput[];
  attested: boolean;
  duplicateOf?: { id: string; childName: string; capturedAt: string } | null;
}

const field = (r: RecordForValidation, key: string) => r.fields.find(f => f.key === key);
const valueOf = (r: RecordForValidation, key: string) => field(r, key)?.value?.trim() ?? '';

/** Which resolutions a flag offers. Empty means the flag can only be corrected. */
export function resolutionOptions(flag: Flag): { kind: ResolutionKind; label: string }[] {
  if (!flag.resolvable) return [];
  const refer = { kind: 'referred' as const, label: 'Refer to the EPI supervisor — keep this record open' };
  if (flag.code.startsWith('interval-too-short') || flag.code.startsWith('dose-early')) {
    return [
      { kind: 'accept-invalid', label: 'The card is correct — this dose does not count and will be repeated' },
      refer
    ];
  }
  if (flag.code === 'zero-dose') {
    return [
      { kind: 'confirmed-none', label: 'Confirmed with the caregiver: this child has had no doses' },
      { kind: 'history-elsewhere', label: 'This is a replacement card; the history exists in the e-Tracker' },
      refer
    ];
  }
  return [refer];
}

export function lowConfidenceDoses(r: RecordForValidation): DoseState[] {
  return r.doses.filter(d => d.confidence < CONFIDENCE_FLOOR && !d.resolved);
}

export function validateRecord(r: RecordForValidation, asOf: string): Flag[] {
  const flags: Flag[] = [];
  const push = (f: Flag) => flags.push(f);
  const rawDob = valueOf(r, 'dob');
  const dob = isIsoDate(rawDob) ? rawDob : '';

  if (!valueOf(r, 'epiId') && !r.newEnrolment) {
    push({
      code: 'no-identifier', severity: 'conflict', blocking: true, resolvable: false,
      title: 'Not yet matched to the registry',
      detail: 'The card carries a registration number, not an e-Tracker ID. Find this child in the registry extract by name, mother or date of birth, or mark them for new enrolment.'
    });
  }

  /* ------------------------------------------------ date of birth itself */
  if (rawDob && !dob) {
    push({
      code: 'invalid-dob', severity: 'conflict', blocking: true, resolvable: false,
      title: 'Date of birth cannot be read as a date',
      detail: `It reads "${rawDob}". Every age and interval check depends on this date, so none of them can run until it is a real calendar date (YYYY-MM-DD).`
    });
  }
  if (dob && daysBetween(dob, asOf) < 0) {
    push({
      code: 'dob-in-future', severity: 'conflict', blocking: true, resolvable: false,
      title: 'Date of birth is in the future',
      detail: `Date of birth reads ${dob}, which is after today. It has been misread.`
    });
  }

  /* ------------------------------------------------- each dose on its own */
  const seen = new Set<string>();
  const readable: DoseState[] = [];
  for (const d of r.doses) {
    const entry = SCHEDULE_BY_KEY[d.antigen];

    if (!entry) {
      push({
        code: `unknown-antigen:${d.antigen}`, severity: 'conflict', blocking: true, resolvable: false,
        title: `"${d.antigen}" is not on the national schedule`,
        detail: 'A dose recorded under a name the schedule does not know is skipped by every interval and age check. Record it under its scheduled name.'
      });
      continue;
    }
    if (seen.has(d.antigen)) {
      push({
        code: `duplicate-antigen:${d.antigen}`, severity: 'conflict', blocking: true, resolvable: false,
        title: `${d.antigen} recorded more than once`,
        detail: `Only one ${d.antigen} can be recorded. Remove the duplicate row.`
      });
      continue;
    }
    seen.add(d.antigen);

    if (!d.givenOn.trim()) {
      push({
        code: `missing-date:${d.antigen}`, severity: 'conflict', blocking: true, resolvable: false,
        title: `${d.antigen} has no date`,
        detail: `Enter the date ${d.antigen} was given, or remove the row. A dose with no date would be exported as given on no day at all, and would hide any conflict its real date would raise.`
      });
      continue;
    }
    if (!isIsoDate(d.givenOn)) {
      push({
        code: `invalid-date:${d.antigen}`, severity: 'conflict', blocking: true, resolvable: false,
        title: `${d.antigen} date cannot be read as a date`,
        detail: `It reads "${d.givenOn}". Until it is a real calendar date, the interval and age checks for this dose cannot run.`
      });
      continue;
    }
    readable.push(d);

    if (dob && daysBetween(dob, d.givenOn) < 0) {
      push({
        code: `dose-before-birth:${d.antigen}`, severity: 'conflict', blocking: true, resolvable: false,
        title: `${d.antigen} recorded before date of birth`,
        detail: `${d.antigen} reads ${d.givenOn}; date of birth reads ${dob}. One of the two has been misread.`
      });
    }
    if (daysBetween(d.givenOn, asOf) < 0) {
      push({
        code: `dose-in-future:${d.antigen}`, severity: 'conflict', blocking: true, resolvable: false,
        title: `${d.antigen} dated in the future`,
        detail: `${d.antigen} reads ${d.givenOn}, which is after today.`
      });
    }
    // Minimum AGE governs only the first dose of a series and single-dose
    // vaccines. The validity of Penta-2, Penta-3 and the rest is governed by the
    // minimum INTERVAL from the previous dose (checked below) — WHO sets no
    // separate minimum age for them, and the age they are scheduled at is a
    // target, not a floor. Checking later doses against their scheduled age
    // would call valid doses invalid and bury the worker in duplicate conflicts.
    // The 4-day allowance is WHO's grace period for doses given slightly early.
    const ageGoverns = !entry.series || entry.n === 1;
    if (ageGoverns && dob && daysBetween(dob, d.givenOn) >= 0) {
      const ageAtDose = daysBetween(dob, d.givenOn);
      if (ageAtDose < entry.dueDays - EARLY_TOLERANCE_DAYS) {
        // Blocking, on the same footing as a short interval: a dose given below
        // its minimum age is not a valid dose and has to be repeated.
        push({
          code: `dose-early:${d.antigen}`, severity: 'conflict', blocking: true, resolvable: true,
          title: `${d.antigen} given below the minimum age`,
          detail: `Given at ${ageAtDose} days of age; the minimum age for ${d.antigen} is ${entry.dueDays} days. A dose given this early does not count and must be repeated — or the date has been misread.`
        });
      }
    }
  }

  /* --------------------------------------------------- series intervals */
  for (const series of ['Penta', 'OPV', 'PCV', 'MR'] as const) {
    const inSeries = SCHEDULE
      .filter(s => s.series === series)
      .map(s => ({ entry: s, dose: readable.find(d => d.antigen === s.key) }))
      .filter((x): x is { entry: typeof SCHEDULE[number]; dose: DoseState } => !!x.dose)
      .sort((a, b) => a.entry.n - b.entry.n);

    for (let i = 1; i < inSeries.length; i++) {
      const prev = inSeries[i - 1]!;
      const cur = inSeries[i]!;
      const gap = daysBetween(prev.dose.givenOn, cur.dose.givenOn);
      if (gap < 0) {
        push({
          code: `series-out-of-order:${cur.entry.key}`, severity: 'conflict', blocking: true, resolvable: false,
          title: `${cur.entry.key} dated before ${prev.entry.key}`,
          detail: `Doses are out of order on the card: ${prev.entry.key} ${prev.dose.givenOn}, ${cur.entry.key} ${cur.dose.givenOn}. One of the two dates has been misread.`
        });
      } else if (gap < MIN_INTERVAL_DAYS) {
        push({
          code: `interval-too-short:${cur.entry.key}`, severity: 'conflict', blocking: true, resolvable: true,
          title: `${cur.entry.key} is ${gap} days after ${prev.entry.key}`,
          detail: `The minimum interval between ${series} doses is ${MIN_INTERVAL_DAYS} days. At ${gap} days this dose does not count and must be repeated — or one of the two dates has been misread.`
        });
      }
    }
  }

  /* ------------------------------------------------------ child overall */
  if (dob && daysBetween(dob, asOf) >= 0) {
    const overdue = overdueDoses(dob, readable, asOf);
    if (overdue.length) {
      const measles = overdue.some(o => o.series === 'MR');
      push({
        code: 'doses-overdue', severity: measles ? 'conflict' : 'warning', blocking: false, resolvable: false,
        title: `${overdue.length} scheduled dose${overdue.length > 1 ? 's' : ''} overdue`,
        detail: overdue.map(o => `${o.label} (due ${scheduledDateFor(dob, o.key)})`).join(' · ') +
          (measles ? ' — measles-containing dose missing.' : '')
      });
    }
    if (!r.doses.length && daysBetween(dob, asOf) > 42) {
      push({
        code: 'zero-dose', severity: 'conflict', blocking: true, resolvable: true,
        title: 'No dose history on the card',
        detail: `Child is ${ageLabel(dob, asOf)} old with no recorded dose. If the registry holds no record either, this is a zero-dose child.`
      });
    }
  }

  /* ------------------------------------------------- reading confidence */
  const lowFields = r.fields.filter(f => f.confidence < CONFIDENCE_FLOOR && !f.resolved);
  const lowDoses = lowConfidenceDoses(r);
  if (lowFields.length || lowDoses.length) {
    const names = [...lowFields.map(f => f.label), ...lowDoses.map(d => `${d.antigen} date`)];
    push({
      code: 'low-confidence', severity: 'conflict', blocking: false, resolvable: false,
      title: `${names.length} reading${names.length > 1 ? 's' : ''} below the confidence threshold`,
      detail: `${names.join(' · ')} — read back from the card and confirm or correct before committing.`
    });
  }

  if (r.duplicateOf) {
    push({
      code: 'duplicate-identifier', severity: 'warning', blocking: false, resolvable: false,
      title: 'Identifier already captured today',
      detail: `${valueOf(r, 'epiId')} was captured at ${r.duplicateOf.capturedAt} for ${r.duplicateOf.childName}.`
    });
  }

  return flags;
}

export interface GateCheck {
  key: string;
  label: string;
  passed: boolean;
  detail?: string;
}

/** Flags that say "this dose is not a valid dose" — for whatever reason. */
const DOSE_INVALIDITY = ['interval-too-short:', 'dose-early:'];
export const invalidDoseAntigen = (code: string): string | null => {
  const family = DOSE_INVALIDITY.find(p => code.startsWith(p));
  return family ? code.slice(family.length) : null;
};

/** Whether a flag has been settled — by correction, or by a resolution that settles it. */
export function isSettled(flag: Flag, resolutions: ResolutionInput[]): boolean {
  if (!flag.blocking) return true;
  if (!flag.resolvable) return false;
  if (resolutions.some(r => r.flagCode === flag.code && SETTLING_KINDS.includes(r.kind))) return true;

  // "This dose does not count and will be repeated" is a decision about the
  // DOSE, not about one reason it is invalid. Penta-3 at 18 days after Penta-2
  // is often also below Penta-3's minimum age; asking the worker to make the
  // same decision twice would be the tool being pedantic at a courtyard session.
  const antigen = invalidDoseAntigen(flag.code);
  return !!antigen && resolutions.some(r =>
    r.kind === 'accept-invalid' && invalidDoseAntigen(r.flagCode) === antigen);
}

/** The conditions a record must satisfy before it may be committed. */
export function commitGate(r: RecordForValidation, asOf: string): GateCheck[] {
  const flags = validateRecord(r, asOf);
  const lowFields = r.fields.filter(f => f.confidence < CONFIDENCE_FLOOR && !f.resolved);
  const lowDoses = lowConfidenceDoses(r);
  const unsettled = flags.filter(f => !isSettled(f, r.resolutions));
  const mustCorrect = unsettled.filter(f => !f.resolvable);
  const referred = unsettled.filter(f => r.resolutions.some(x => x.flagCode === f.code && x.kind === 'referred'));
  const dob = valueOf(r, 'dob');

  const unread = [...lowFields.map(f => f.label), ...lowDoses.map(d => `${d.antigen} date`)];

  return [
    {
      key: 'identity',
      label: 'Child name and a readable date of birth present',
      passed: !!valueOf(r, 'childName') && isIsoDate(dob)
    },
    {
      key: 'registry',
      label: 'Registry identifier present, or child marked for new enrolment',
      passed: !!valueOf(r, 'epiId') || r.newEnrolment
    },
    {
      key: 'confidence',
      label: 'Low-confidence readings read back from the card',
      passed: unread.length === 0,
      detail: unread.length ? `${unread.join(', ')} still to read back` : undefined
    },
    {
      key: 'conflicts',
      label: 'Conflicts corrected or resolved by the worker',
      passed: unsettled.length === 0,
      detail: unsettled.length
        ? [
            mustCorrect.length ? `${mustCorrect.length} must be corrected` : '',
            referred.length ? `${referred.length} referred to the supervisor` : '',
            unsettled.length - mustCorrect.length - referred.length > 0
              ? `${unsettled.length - mustCorrect.length - referred.length} awaiting a decision` : ''
          ].filter(Boolean).join(' · ')
        : undefined
    },
    {
      key: 'attestation',
      label: 'Worker attests the entry matches the card in hand',
      passed: r.attested
    }
  ];
}

export function canCommit(r: RecordForValidation, asOf: string): boolean {
  return commitGate(r, asOf).every(c => c.passed);
}
